import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tooltip", "content"]

  connect() {
    this.isMobile = window.innerWidth <= 768
    this.setupEventListeners()
    if (!this.isMobile) {
      this.updateTooltipPlacement()
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    this.cleanupEventListeners()
  }

  setupEventListeners() {
    this._boundResize = this.handleWindowResize.bind(this);
    window.addEventListener('resize', this._boundResize)

    if (this.isMobile) {
      this._boundClickOutside = this.handleClickOutside.bind(this)
      document.addEventListener('click', this._boundClickOutside)
    }
  }

  cleanupEventListeners() {
    window.removeEventListener('resize', this._boundResize)

    if (this.isMobile && this._boundClickOutside) {
      document.removeEventListener('click', this._boundClickOutside)
    }
  }

  handleWindowResize() {
    this.checkMobile();
    if (!this.isMobile) {
      this.updateTooltipPlacement();
    }
  }

  checkMobile() {
    const newIsMobile = window.innerWidth <= 768
    if (newIsMobile !== this.isMobile) {
      this.isMobile = newIsMobile
      this.cleanupEventListeners()
      this.setupEventListeners()
    }
  }

  toggle(e) {
    e.stopPropagation()
    if (this.isMobile) {
      this.tooltipTarget.classList.toggle('active')
      this.updateTooltipPlacement()
    }
  }

  // Add these methods for desktop hover
  handleMouseEnter() {
    if (!this.isMobile) {
      this.tooltipTarget.classList.add('active')
      this.updateTooltipPlacement()
    }
  }

  handleMouseLeave() {
    if (!this.isMobile) {
      this.tooltipTarget.classList.remove('active')
    }
  }

  handleClickOutside(event) {
    if (this.isMobile && this.tooltipTarget.classList.contains('active')) {
      const container = this.tooltipTarget
      if (!container.contains(event.target)) {
        this.tooltipTarget.classList.remove('active')
      }
    }
  }

  updateTooltipPlacement() {
    this.contentTarget.classList.remove('tooltip-right', 'tooltip-left', 'tooltip-default');
    const trigger = this.tooltipTarget.querySelector('span[data-action]');
    const tooltip = this.contentTarget;

    if (!trigger || !tooltip) return;

    function findOverflowHiddenAncestor(el) {
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        const overflow = style.overflow + style.overflowX + style.overflowY;
        if (/(hidden|auto|scroll)/.test(overflow)) {
          return el;
        }
        el = el.parentElement;
      }
      return document.body;
    }

    const triggerRect = trigger.getBoundingClientRect();
    // Tooltip is "display:none" by default, so to measure safely:
    tooltip.style.visibility = 'hidden';
    tooltip.style.display = 'block';
    const tooltipRect = tooltip.getBoundingClientRect();

    // Get ancestor which clips overflow
    const overflowAncestor = findOverflowHiddenAncestor(tooltip.parentElement);
    const ancestorRect = overflowAncestor.getBoundingClientRect();

    // Mobile: check if showing above would overflow ancestor's top; if so, display below instead
    const isMobile = window.innerWidth <= 768;
    const tooltipMargin = 8;
    if (isMobile) {
      // Calculate where the tooltip would appear (centered above)
      const popoverAboveTop = triggerRect.top - tooltipRect.height - tooltipMargin;

      tooltip.style.left = '';
      tooltip.style.right = '';
      tooltip.style.top = '';
      tooltip.style.bottom = '';
      tooltip.style.transform = '';
      this.contentTarget.classList.remove('tooltip-above-left', 'tooltip-above-right', 'tooltip-above-center', 'tooltip-mobile-below', 'tooltip-right', 'tooltip-left', 'tooltip-top-right', 'tooltip-default');

      console.log(popoverAboveTop < ancestorRect.top)
      if (popoverAboveTop < ancestorRect.top) {
        // Overflowing at the top, show below (slide down)
        this.contentTarget.classList.add('tooltip-mobile-below');
        this.contentTarget.style.left = '50%';
        this.contentTarget.style.right = 'auto';
        this.contentTarget.style.top = `calc(100% + ${tooltipMargin}px)`;
        this.contentTarget.style.bottom = 'auto';
        this.contentTarget.style.transform = 'translateX(-50%)';
      } else {
        // Safe to show above, centered
        this.contentTarget.classList.add('tooltip-above-center');
        this.contentTarget.style.left = '50%';
        this.contentTarget.style.right = 'auto';
        this.contentTarget.style.top = 'auto';
        this.contentTarget.style.bottom = `calc(100% + ${tooltipMargin}px)`;
        this.contentTarget.style.transform = 'translateX(-50%)';
      }

      tooltip.style.visibility = '';
      tooltip.style.display = '';
      return;
    }

    // For above alignment (desktop):
    // Try above-left: popover bottom aligns to trigger top, left aligns to trigger left
    const aboveLeftLeft = triggerRect.left;
    const aboveLeftRight = triggerRect.left + tooltipRect.width;
    const aboveTop = triggerRect.top - tooltipRect.height - tooltipMargin;
    const fitsAboveLeft =
      aboveLeftLeft >= ancestorRect.left &&
      aboveLeftRight <= ancestorRect.right &&
      aboveTop >= ancestorRect.top;

    // Remove previously set style/class in case
    tooltip.style.left = '';
    tooltip.style.right = '';
    tooltip.style.top = '';
    tooltip.style.bottom = '';
    tooltip.style.transform = '';
    this.contentTarget.classList.remove('tooltip-above-left', 'tooltip-above-right', 'tooltip-above-center', 'tooltip-mobile-below', 'tooltip-right', 'tooltip-left', 'tooltip-top-right', 'tooltip-default');

    if (fitsAboveLeft) {
      this.contentTarget.classList.add('tooltip-above-left');
      this.contentTarget.style.left = '0';
      this.contentTarget.style.right = 'auto';
      this.contentTarget.style.top = 'auto';
      this.contentTarget.style.bottom = `calc(100% + ${tooltipMargin}px)`;
      this.contentTarget.style.transform = 'none';
    } else {
      this.contentTarget.classList.add('tooltip-above-right');
      this.contentTarget.style.left = 'auto';
      this.contentTarget.style.right = '0';
      this.contentTarget.style.top = 'auto';
      this.contentTarget.style.bottom = `calc(100% + ${tooltipMargin}px)`;
      this.contentTarget.style.transform = 'none';
    }

    // Restore actual visibility state
    tooltip.style.visibility = '';
    tooltip.style.display = '';
  }
}
